import { useEffect, useState } from "react";
import { SERVY_API_URL } from "../constants/api";

/**
 * The AI service answers every request without these headers with 400
 * ("app must be a string"); the dashboard sends the same values.
 */
const SERVY_API_HEADERS = { app: "servroom", version: "1" };

export const buildServyQuestionListUrl = ({ roomUuid, language }) =>
  `${SERVY_API_URL}/knowledge-base/question-list?${new URLSearchParams({
    room_uuid: roomUuid,
    language,
  })}`;

/*
 * question-list answers in the AI service's own envelope (staging, 2026-10-26):
 *   { status: 200, message: "OK", body: { room_uuid, language,
 *     KnowledgeBaseList: [{ qa_id, question, answer }] } }
 * Only the question is shown; a tapped one is asked like a typed one, so
 * Servy answers it in the chat.
 */
const questionText = (item) =>
  typeof item?.question === "string" ? item.question : "";

/**
 * The questions in a question-list response, in the order sent, trimmed and
 * without blanks or repeats. Any other response is an empty list.
 */
export const parseServyQuestions = (response) => {
  const list = response?.body?.KnowledgeBaseList;
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list
    .map((item) => questionText(item).trim())
    .filter((text) => {
      if (!text || seen.has(text)) return false;
      seen.add(text);
      return true;
    });
};

const cache = new Map();

/** For tests: forget the questions loaded so far. */
export const clearServyQuestionsCache = () => cache.clear();

/**
 * Suggested questions for a unit in one of Servy's languages (a value from
 * toServyLangCode). Resolves to [] when the request fails or the unit has
 * none, and never rejects: suggestions are optional, so a failure shows no
 * error. Only successful lists are cached, so a later visit retries.
 */
export const fetchServyQuestions = ({ roomUuid, language }) => {
  if (!roomUuid || !language || !SERVY_API_URL) return Promise.resolve([]);

  const key = `${roomUuid}\u0000${language}`;
  if (cache.has(key)) return Promise.resolve(cache.get(key));

  return fetch(buildServyQuestionListUrl({ roomUuid, language }), {
    headers: SERVY_API_HEADERS,
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((body) => {
      if (body == null) return [];
      const questions = parseServyQuestions(body);
      cache.set(key, questions);
      return questions;
    })
    .catch(() => []);
};

/**
 * The suggested questions for Servy's first-time screen. They reload when the
 * language changes; until they arrive, and whenever there are none, it's [].
 */
export const useServySuggestedQuestions = ({
  roomUuid,
  language,
  enabled = true,
}) => {
  const [loaded, setLoaded] = useState({ key: null, questions: [] });
  const key = enabled && roomUuid && language ? `${roomUuid}:${language}` : null;

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    fetchServyQuestions({ roomUuid, language }).then((questions) => {
      if (!cancelled) setLoaded({ key, questions });
    });
    return () => {
      cancelled = true;
    };
  }, [key, roomUuid, language]);

  // A list for another unit or language is never shown, not even briefly
  return key && loaded.key === key ? loaded.questions : [];
};
